import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import OfficeScene from './OfficeScene';
import './OfficeVirtualModal.css';
import AppIcon from '../icons/AppIcon';

/**
 * Modal fullscreen para o Escritório Virtual 3D
 * Renderizado via Portal para ficar acima de tudo
 */
const OfficeVirtualModal = ({ isOpen, onClose, projectId, officeConfig }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Lock scroll quando modal está aberto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle ESC key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Handle loading complete
  const handleSceneReady = useCallback(() => {
    setIsLoading(false);
  }, []);

  // Handle error
  const handleError = useCallback((err) => {
    console.error('[OfficeVirtual] Erro:', err);
    setError(err.message || 'Erro ao carregar escritório');
    setIsLoading(false);
  }, []);

  if (!isOpen) return null;

  const modalContent = (
    <div className="office-virtual-overlay">
      {/* Loading State */}
      {isLoading && (
        <div className="office-virtual-loading">
          <div className="office-loading-spinner"></div>
          <p>Carregando escritório virtual...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="office-virtual-error">
          <AppIcon name="error" />
          <p>{error}</p>
          <button onClick={onClose} className="office-error-btn">
            Fechar
          </button>
        </div>
      )}

      {/* Header */}
      <div className="office-virtual-header">
        <div className="office-header-left">
          <button onClick={onClose} className="office-exit-btn">
            <AppIcon name="arrow_back" />
            <span>Sair do Escritório</span>
          </button>
          <h2 className="office-title">Escritório Virtual</h2>
        </div>
        <div className="office-header-right">
          <div className="office-online-indicator">
            <span className="office-online-dot"></span>
            <span>Você está online</span>
          </div>
        </div>
      </div>

      {/* 3D Scene */}
      {!error && (
        <div className="office-virtual-scene">
          <OfficeScene
            projectId={projectId}
            officeConfig={officeConfig}
            onReady={handleSceneReady}
            onError={handleError}
          />
        </div>
      )}

      {/* HUD - Bottom Controls */}
      <div className="office-virtual-hud">
        <div className="office-hud-left">
          <div className="office-controls-hint">
            <span>WASD</span> Mover
            <span>Shift</span> Correr
            <span>Space</span> Pular
            <span>Mouse</span> Olhar
            <span>ESC</span> Sair
          </div>
        </div>
        <div className="office-hud-right">
          <button className="office-hud-btn" title="Chat">
            <AppIcon name="chat" />
          </button>
          <button className="office-hud-btn" title="Configurações">
            <AppIcon name="settings" />
          </button>
        </div>
      </div>
    </div>
  );

  // Render via Portal
  return createPortal(modalContent, document.body);
};

export default OfficeVirtualModal;
